import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { mastersApi, fetchActiveUsers, userLabel } from '../../services/expenseMastersApi';

// Active Phase 1 masters for the voucher form. Heads are loaded per category on demand.
// Vendors are searched server-side by the line editor.
//
// Dropdown order follows erp_expense: currencies and heads in ERP id order (records added
// in Quonnect after the import follow, by name); Paid Via in creation (seed) order.
const byLegacyThen = (key, fallback) => (a, b) => {
  const x = a.legacy?.[key] ?? Infinity;
  const y = b.legacy?.[key] ?? Infinity;
  return x !== y ? x - y : String(a[fallback]).localeCompare(String(b[fallback]));
};
const useVoucherLookups = () => {
  const [state, setState] = useState({
    loading: true,
    categories: [],
    countries: [],
    operatingCountries: [],
    currencies: [],
    paidVia: [],
    users: [],
  });
  const [heads, setHeads] = useState({});
  const requested = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = (name, fn) => fn().catch((e) => {
      toast.error(`Failed to load ${name}: ${e.message}`);
      return null;
    });

    Promise.all([
      load('expense categories', () => mastersApi.list('categories', { limit: 500 })),
      load('countries', () => mastersApi.list('countries', { limit: 500 })),
      load('currencies', () => mastersApi.list('currencies', { limit: 500 })),
      load('paid via', () => mastersApi.list('paidVia', { limit: 500 })),
      load('employees', () => fetchActiveUsers()),
    ]).then(([categories, countries, currencies, paidVia, users]) => {
      if (cancelled) return;
      const countryList = countries?.data || [];
      setState({
        loading: false,
        categories: (categories?.data || []).map((c) => ({ value: c._id, label: c.name })),
        countries: countryList,
        operatingCountries: countryList.filter((c) => c.isOperatingCountry).map((c) => ({ value: c._id, label: c.name })),
        // erp_expense shows "<symbol> <code>", e.g. "₹ INR"
        currencies: [...(currencies?.data || [])].sort(byLegacyThen('erpId', 'code'))
          .map((c) => ({ value: c.code, label: c.symbol && c.symbol !== c.code ? `${c.symbol} ${c.code}` : c.code })),
        paidVia: [...(paidVia?.data || [])].sort((a, b) => String(a._id).localeCompare(String(b._id)))
          .map((p) => ({ value: p._id, label: p.name })),
        users: (Array.isArray(users) ? users : []).map((u) => ({ value: u._id, label: userLabel(u) })),
      });
    });

    return () => { cancelled = true; };
  }, []);

  const loadHeads = useCallback((categoryId) => {
    if (!categoryId || requested.current.has(categoryId)) return;
    requested.current.add(categoryId);
    mastersApi.list('heads', { categoryId, limit: 500 })
      .then((res) => setHeads((prev) => ({ ...prev, [categoryId]: [...(res.data || [])].sort(byLegacyThen('erpHeadId', 'name')) })))
      .catch((e) => {
        requested.current.delete(categoryId);
        toast.error(`Failed to load expense heads: ${e.message}`);
      });
  }, []);

  const headsFor = useCallback((categoryId) => heads[categoryId] || [], [heads]);

  return { ...state, headsFor, loadHeads };
};

export default useVoucherLookups;
